import React, { useState } from 'react';
import { Menu, X } from 'lucide-react';
import logo from '../Assets/ToiceLogoTransparent.png';

const Navigation = ({ currentView, setCurrentView, user, colors, showBackButton = false, onLogout, accountType, demoMode = false, onExitDemo }) => {
  const [menuOpen, setMenuOpen] = useState(false);

  // ── Minimal demo bar ────────────────────────────────────────────────────────
  if (demoMode) {
    return (
      <header style={{ backgroundColor: '#0f172a', borderBottom: '1px solid #1e293b', padding: '0.75rem 1.5rem', position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <img src={logo} alt="Toice Logo" style={{ height: '52px', width: 'auto', padding: '6px', backgroundColor: 'white', borderRadius: '6px' }} />
            <div>
              <div style={{ fontSize: '1rem', fontWeight: 'bold', color: 'white', lineHeight: 1.2 }}>Toice Risk Intelligence</div>
              <div style={{ fontSize: '0.65rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Interactive Demo</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <button
              onClick={() => setCurrentView('pricing')}
              style={{ padding: '0.5rem 1.25rem', backgroundColor: 'transparent', color: '#94a3b8', border: '1px solid #334155', borderRadius: '0.5rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem' }}
            >
              See Pricing
            </button>
            <button
              onClick={() => { if (onExitDemo) onExitDemo(); else setCurrentView('landing'); }}
              style={{ padding: '0.5rem 1.25rem', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem' }}
            >
              Exit Demo
            </button>
          </div>
        </div>
      </header>
    );
  }
  // ────────────────────────────────────────────────────────────────────────────

  const navigationLinks = [
    { name: 'Home', view: 'landing', show: true },
    { name: 'For Tenants', view: 'tenants', show: true },
    { name: 'Products & Pricing', view: 'pricing', show: true },
    { name: 'Portfolio Dashboard', view: 'dashboard', show: !demoMode },
    { name: 'Try Demo', view: 'demo', show: !user && !demoMode },
    { name: 'My Portfolio', view: 'lender-dashboard', show: !!user && accountType === 'lender' },
    { name: 'Property Manager', view: 'pm-dashboard', show: !!user && accountType !== 'lender' },
    { name: 'Admin', view: 'admin', show: !!user && accountType === 'admin' },
  ].filter(link => link.show);

  return (
    <header style={{
      backgroundColor: 'white',
      borderBottom: '1px solid #e2e8f0',
      padding: '1rem',
      position: 'sticky',
      top: 0,
      zIndex: 50
    }}>
      <div style={{
        maxWidth: '1280px',
        margin: '0 auto',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        {/* Logo and Title */}
        <div
          style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}
          onClick={() => setCurrentView('landing')}
        >
          <img src={logo} alt="Toice Logo" style={{ height: '80px', width: 'auto', padding: '8px', backgroundColor: 'white', borderRadius: '8px' }} />
          <div>
            <h1 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#0f172a', margin: 0 }}>
              Toice Risk Intelligence
            </h1>
            <p style={{ fontSize: '0.625rem', color: '#64748b', margin: 0 }}>
              Risk Analytics Platform
            </p>
          </div>
        </div>

        {/* Desktop Navigation */}
        <nav style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {/* Navigation Links - Desktop */}
          <div style={{
            display: 'flex',
            gap: '0.5rem'
          }} className="desktop-nav">
            {navigationLinks.map(link => (
              <button
                key={link.view}
                onClick={() => {
                  setCurrentView(link.view);
                  setMenuOpen(false);
                }}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: currentView === link.view ? colors.primary : 'transparent',
                  color: currentView === link.view ? 'white' : '#475569',
                  border: 'none',
                  borderRadius: '0.375rem',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '0.875rem',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (currentView !== link.view) {
                    e.target.style.backgroundColor = '#f1f5f9';
                  }
                }}
                onMouseLeave={(e) => {
                  if (currentView !== link.view) {
                    e.target.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {link.name}
              </button>
            ))}

            {/* Logged-in user info + logout */}
            {user && onLogout && (
              <>
                <span style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem', color: '#64748b', backgroundColor: '#f1f5f9', borderRadius: '0.375rem', marginLeft: '0.5rem' }}>
                  {user.email}
                </span>
                <button
                  onClick={onLogout}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: 'transparent',
                    color: '#ef4444',
                    border: '1px solid #ef4444',
                    borderRadius: '0.375rem',
                    cursor: 'pointer',
                    fontWeight: '500',
                    fontSize: '0.875rem',
                  }}
                >
                  Sign Out
                </button>
              </>
            )}

            {/* Sign In Button (when not logged in) */}
            {!user && (
              <button
                onClick={() => setCurrentView('login')}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: colors.primary,
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '0.875rem',
                  marginLeft: '0.5rem'
                }}
              >
                Sign In
              </button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0.5rem',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: '#475569'
            }}
            className="mobile-menu-btn"
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
      </div>

      {/* Mobile Dropdown Menu */}
      {menuOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          backgroundColor: 'white',
          borderBottom: '1px solid #e2e8f0',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          padding: '1rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem'
        }}>
          {navigationLinks.map(link => (
            <button
              key={link.view}
              onClick={() => {
                setCurrentView(link.view);
                setMenuOpen(false);
              }}
              style={{
                padding: '0.75rem 1rem',
                backgroundColor: currentView === link.view ? colors.primary : 'transparent',
                color: currentView === link.view ? 'white' : '#475569',
                border: 'none',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                fontWeight: '500',
                textAlign: 'left',
                width: '100%'
              }}
            >
              {link.name}
            </button>
          ))}

          {user && onLogout && (
            <button
              onClick={() => {
                onLogout();
                setMenuOpen(false);
              }}
              style={{
                padding: '0.75rem 1rem',
                backgroundColor: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                fontWeight: '500',
                textAlign: 'left',
                width: '100%'
              }}
            >
              Sign Out
            </button>
          )}

          {!user && (
            <button
              onClick={() => {
                setCurrentView('login');
                setMenuOpen(false);
              }}
              style={{
                padding: '0.75rem 1rem',
                backgroundColor: colors.primary,
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                fontWeight: '600',
                textAlign: 'left',
                width: '100%'
              }}
            >
              Sign In
            </button>
          )}
        </div>
      )}

      {/* CSS for responsive behavior */}
      <style>{`
        @media (min-width: 768px) {
          .desktop-nav {
            display: flex !important;
          }
          .mobile-menu-btn {
            display: none !important;
          }
        }
      `}</style>
    </header>
  );
};

export default Navigation;
